import { Target, Heart, Shield, Users } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { aboutContent } from '../mockData';

export const About = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            About Phoenix Medical Supply
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Building reliable connections in the pharmaceutical supply industry
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Mission</h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              {aboutContent.mission}
            </p>
          </div>

          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Story</h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              {aboutContent.story}
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Core Values
            </h2>
            <p className="text-lg text-gray-600">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="border-gray-200 bg-white hover:shadow-lg transition-shadow">
              <CardContent className="pt-8 pb-6 px-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">
                      Quality First
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      We only work with certified manufacturers who meet strict quality standards.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-gray-200 bg-white hover:shadow-lg transition-shadow">
              <CardContent className="pt-8 pb-6 px-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Target className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">
                      Transparency
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      Clear communication and honest pricing throughout the entire process.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-gray-200 bg-white hover:shadow-lg transition-shadow">
              <CardContent className="pt-8 pb-6 px-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Heart className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">
                      Reliability
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      Consistent supply and on-time delivery you can count on.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-gray-200 bg-white hover:shadow-lg transition-shadow">
              <CardContent className="pt-8 pb-6 px-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Users className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">
                      Customer Focus
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      Your specific needs drive our sourcing and supply decisions.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              What Sets Us Apart
            </h2>
          </div>

          <div className="space-y-8">
            <div className="border-l-4 border-orange-600 pl-6 py-2">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Extensive Manufacturer Network
              </h3>
              <p className="text-gray-700 leading-relaxed">
                We've built strong relationships with certified pharmaceutical manufacturers across multiple regions, giving you access to a wide range of quality medicines.
              </p>
            </div>

            <div className="border-l-4 border-orange-600 pl-6 py-2">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Industry Expertise
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Our team understands both the manufacturing and healthcare sides of the industry, enabling us to bridge communication gaps and find optimal solutions.
              </p>
            </div>

            <div className="border-l-4 border-orange-600 pl-6 py-2">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Commitment to Quality
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Every manufacturer in our network is thoroughly vetted to ensure they meet international quality standards and regulatory compliance.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
